<template>
  <div v-if="listLoading || routeLoading" class="fixed inset-0 bg-black/10 z-40"></div>
  <div class="h-screen flex flex-col bg-gray-50">
    <Header />

    <SearchFilterBar
      v-model:view-mode="viewMode"
      v-model:filters="filters"
      :filters="filters"
      :facets="data?.facets"
      :place="place"
      @toggle-filters="showFilters = !showFilters"
    >
      <template #search-box>
        <SearchBox
          v-model="query"
          @select="path => navigate('/' + navStore.mode + '/' + path + '/search')"
        />
      </template>
      <template #filters-panel>
        <SearchFilterPanel
          v-model:filters="filters"
          :show="showFilters"
          :facets="data.facets"
          :tag-groups="tagGroups"
          :loading-tags="labelsLoading"
          @clear-filters="clearFilters"
        />
      </template>
    </SearchFilterBar>

    <div class="flex-1 overflow-hidden relative">
      <div v-if="viewMode === 'map' || viewMode === 'list'" class="h-full flex flex-col-reverse lg:flex-row">
        <!-- Results List -->
        <div
          v-if="viewMode === 'list' || viewMode === 'map' || !isMobile"
          ref="scroll"
          class="bg-white border-r border-gray-200 z-30 transition-all duration-300"
          :class="[
            isMobile
              ? (showMobileList || viewMode === 'list' ? 'absolute inset-0 w-full overflow-y-auto' : 'hidden')
              : (viewMode === 'map' ? 'relative w-[480px] overflow-y-auto' : 'w-full lg:w-[55%] xl:w-[60%] overflow-y-auto')
          ]"
        >
          <div v-if="viewMode === 'list' && activeProperty" class="sticky top-0 z-20 shadow-md">
            <FeaturedCard :property="activeProperty" :agents="getAgentProfilesFor(activeAgents)" photo-priority="zumen" />
          </div>

          <div class="flex flex-col p-4 border-b border-gray-200">
            <div v-if="isMobile && viewMode === 'map'" class="flex justify-end mb-4">
              <button class="px-4 py-2 bg-brand text-white rounded-full font-semibold shadow-lg text-sm" @click="showMobileList = false">
                {{ t('searchResults.showMap') }}
              </button>
            </div>
            <Breadcrumbs class="bg-white !mx-0 mb-5" />
            <h1 class="font-semibold text-gray-900 flex items-center gap-2">
              <template v-if="countLoading">
                <span class="inline-block w-24 h-6 bg-gray-200 animate-pulse rounded"></span>
              </template>
              <template v-else>
                {{ placeName ? t('sections.nPropertiesIn', { count: total, place: placeName }) : t('sections.nProperties', { count: total }) }}
              </template>
            </h1>
            <div class="flex flex-wrap items-center gap-3 mt-2 mb-2">
              <a
                v-if="page?.data?.openMarketPath"
                :href="`https://market.openrm.jp/${page.data.openMarketPath}`"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full hover:bg-gray-200"
              >
                <img src="/assets/favicon.svg" class="w-3 h-3" alt="" />
                {{ t('property.view_livability_data') }}
              </a>
              <a
                v-if="page?.children?.length"
                :href="'/' + pageContext.routeParams.path"
                class="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
              >
                {{ placeName ? t('sections.viewAllAreasIn', { place: placeName }) : t('sections.viewAllAreas') }}
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
            <p class="text-sm text-gray-600 mt-1">{{ t('searchResults.sortBy') }}</p>
          </div>

          <div v-if="viewMode === 'map'" class="p-4 space-y-4">
            <PropertyCard v-for="property in buildings" :key="property.id" v-bind="property" :agents="getAgentProfilesFor(property.agents)" photo-priority="zumen" />
          </div>
          <ListTable
            v-else
            :properties="buildings"
            :selected-id="activeProperty?.id"
            @select="setActiveProperty"
          />

          <Pagination v-if="totalPages > 1" :current-page="currentPage" :total-pages="totalPages" class="p-4" @page-change="onPageChange" />

          <!-- JA-Specific: Area Agents -->
          <div v-if="agentsWithProfiles.length" class="p-6 border-t border-gray-200 bg-gray-50/50">
            <h3 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-5 px-1">{{ t('agent.areaAgents') }}</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3">
              <a
                v-for="agent in agentsWithProfiles"
                :key="agent.id"
                :href="`/agent/${agent.organizationId}/${agent.id}`"
                class="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-2xl hover:shadow-lg hover:border-brand/30 transition-all group"
              >
                <div class="relative flex-shrink-0">
                  <ImageWithFallback :src="getAvatar(agent)" class="w-12 h-12 rounded-full border border-gray-100 object-cover shadow-sm" :width="48" :height="48" />
                  <div class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
                </div>
                <div class="min-w-0">
                  <div class="text-base font-bold text-gray-900 group-hover:text-brand transition truncate">{{ agent.profile?.personal?.fullname }}</div>
                  <div class="text-xs text-gray-500 truncate mt-0.5">{{ agent.company?.general?.name }}</div>
                </div>
              </a>
            </div>
          </div>

          <SearchCrossLinks class="border-t border-gray-200" />
          <RelatedContent md="2" lg="2" />
          <Footer :cols="1" />
        </div>

        <!-- Map View -->
        <SearchMapView
          v-if="!isMobile || viewMode === 'map'"
          v-model:map-params="mapParams"
          :mode="navStore.mode"
          :place="place"
          :pins="pins"
          :active-marker-id="activeMarkerId"
          :active-card="activeCard"
          :active-card-agents="getAgentProfilesFor(activeCard?.agents || [])"
          photo-priority="zumen"
          @remove-boundary="removeBoundary"
          @marker-click="onMarkerClick"
          @close-card="activeCard = null"
        >
          <template #mobile-toggle>
            <SearchMobileToggle
              v-if="viewMode === 'map'"
              :view-mode="viewMode"
              :show-mobile-list="showMobileList"
              :total="total"
              @toggle="showMobileList = true"
            />
          </template>
        </SearchMapView>
      </div>

      <!-- Grid View -->
      <div v-else ref="scroll" class="h-full overflow-y-auto">
        <div class="relative max-w-[1600px] mx-auto px-4 py-6 sm:px-6">
          <Breadcrumbs class="bg-transparent !mx-0 mb-5" />
          <div class="mb-4">
            <h1 class="font-semibold text-gray-900 flex items-center gap-2">
              <template v-if="countLoading">
                <span class="inline-block w-24 h-6 bg-gray-200 animate-pulse rounded"></span>
              </template>
              <template v-else>
                {{ placeName ? t('sections.nPropertiesIn', { count: total, place: placeName }) : t('sections.nProperties', { count: total }) }}
              </template>
            </h1>
            <div class="flex flex-wrap items-center gap-3 mt-2 mb-2">
              <a
                v-if="page?.data?.openMarketPath"
                :href="`https://market.openrm.jp/${page.data.openMarketPath}`"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full hover:bg-gray-200"
              >
                <img src="/assets/favicon.svg" class="w-3 h-3" alt="" />
                {{ t('property.view_livability_data') }}
              </a>
              <a
                v-if="page?.children?.length"
                :href="'/' + pageContext.routeParams.path"
                class="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
              >
                {{ placeName ? t('sections.viewAllAreasIn', { place: placeName }) : t('sections.viewAllAreas') }}
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
            <p class="text-sm text-gray-600 mt-1">{{ t('searchResults.sortBy') }}</p>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <PropertyCard v-for="property in buildings" :key="property.id" v-bind="property" direction="col" photo-priority="zumen" :agents="getAgentProfilesFor(property.agents)" />
          </div>
          <Pagination v-if="totalPages > 1" :current-page="currentPage" :total-pages="totalPages" class="mt-8" @page-change="onPageChange" />
        </div>
        <SearchCrossLinks class="border-t border-gray-200" />
        <RelatedContent />
        <Footer />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, useTemplateRef } from "vue";
import { ExternalLink } from 'lucide-vue-next';
import { navigate } from 'vike/client/router'
import { usePageContext } from "vike-vue/usePageContext";
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import type { PageContext } from 'vike/types'
import {
  PropertyCard, FeaturedCard, ListTable, useTags,
  Pagination, Breadcrumbs, SearchCrossLinks, RelatedContent, Footer,
  useMediaQuery, useLoadingStore, useSearchData, SearchFilterBar, SearchFilterPanel,
  SearchMapView, SearchMobileToggle, ImageWithFallback,
  type Pin, type MapParams, type PageDataSearch
} from '@repo/ui';
import Header from '@/components/Header.vue'
import SearchBox from '@/components/SearchBox.vue'
import { useNavStore } from '@repo/ui'
import type { Deal, Agent, AgentWithProfiles } from '@repo/types'

const pageContext = usePageContext() as PageContext<PageDataSearch>;
const { t, locale } = useI18n();
const navStore = useNavStore();
const loadingStore = useLoadingStore();
const { isLoading: routeLoading } = storeToRefs(loadingStore);
const isMobile = useMediaQuery('(max-width: 1023px)');

const {
  data, filters, currentPage, viewMode, listLoading, countLoading, labelsLoading, total, totalPages,
  buildings, pins, place, activeCard, load, getAgentProfilesFor, agentsWithProfiles,
  fetchAgentProfiles
} = useSearchData({ fetchAgents: true });

const query = ref('');
const showFilters = ref(false);
const showMobileList = ref(false);
const scroll = useTemplateRef<HTMLDivElement>("scroll");

const { tagGroups } = useTags(computed(() => data.value?.labels ?? []));

const clearFilters = () => {
  filters.value = { homeType: filters.value.homeType, price: { min: 0, max: undefined }, size: { min: 0, max: undefined }, labels: [] };
  showFilters.value = false;
};

const mapParams = ref<MapParams>();

function onPageChange(page: number) {
  currentPage.value = page;
  scroll.value?.scrollTo(0, 0);
  if (mapParams.value) load(mapParams.value, "listings", (page - 1) * 20);
}

// Watch place.level, not the place object: the listings response returns a fresh `place` on every
// load, which would re-trigger this watcher (and load) forever for line pages.
watch([mapParams, () => place.value?.level, () => navStore.mode, () => pageContext.data?.filters], () => {
  if (mapParams.value && !showFilters.value && (!place.value || place.value.level === 'line')) load(mapParams.value)
}, { immediate: true });

const activeMarkerId = ref<string | null>(null);

async function onMarkerClick(hovered: Pin) {
  activeMarkerId.value = hovered.id;
  const [lng, lat] = hovered.geometry.coordinates;
  const res = await pageContext.api.call("listing", navStore.mode, { id: [hovered.deals[0]?.publicId] });
  activeCard.value = res.data.buildings[0];
  if (activeCard.value?.agents?.length) fetchAgentProfiles(activeCard.value.agents);
}

const activeProperty = ref<Deal | null>(null);
const activeAgents = ref<Agent[]>([]);
const setActiveProperty = (prop: Deal) => {
  activeProperty.value = prop;
  activeAgents.value = buildings.value.find(b => b.deals?.some(d => d.id === prop.id))?.agents || [];
};
watch(buildings, b => { if (b[0]?.deals?.[0]) setActiveProperty(b[0].deals[0]); }, { immediate: true });

function removeBoundary() { query.value = ''; navigate('/' + navStore.mode + '/search'); }
function getAvatar(agent: AgentWithProfiles) {
  if (agent.profile?.avatar?.url) return agent.profile.avatar.url;
  if (agent.profile?.avatar?.data) return 'data:image/png;base64,' + agent.profile.avatar.data;
  return '/images/avatar.svg';
}
const placeName = computed(() => place.value ? (locale.value === 'ja' ? place.value.name : place.value.roman ?? place.value.name) : null);

const page = computed(() => pageContext.data.page)
</script>
